// Shared field controls for Models create/edit meta forms.
import type { MfgRecipeKind } from "@/lib/manufacturing/types";
import { MFG_RECIPE_KIND_OPTIONS } from "@/lib/manufacturing/recipeKinds";
import { inputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";

export function FieldTagsText({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <label className="block text-xs text-gray-600 dark:text-zinc-300">
      {label}
      <input
        type="text"
        className={`${inputClass} mt-1`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
      />
    </label>
  );
}

export function FieldText({
  label,
  value,
  onChange,
  placeholder,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hint?: string;
}) {
  return (
    <label className="block text-xs text-gray-600 dark:text-zinc-300">
      {label}
      <input
        className={`${inputClass} mt-1`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {hint ? (
        <span className="mt-1 block text-[11px] text-amber-700 dark:text-amber-300">
          {hint}
        </span>
      ) : null}
    </label>
  );
}

export function FieldKind({
  value,
  onChange,
}: {
  value: MfgRecipeKind;
  onChange: (v: MfgRecipeKind) => void;
}) {
  return (
    <label className="block text-xs text-gray-600 dark:text-zinc-300">
      Kind
      <select
        className={`${inputClass} mt-1`}
        value={value}
        onChange={(e) => onChange(e.target.value as MfgRecipeKind)}
      >
        {MFG_RECIPE_KIND_OPTIONS.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Recipe group select. Empty string = ungrouped. */
export function FieldRecipeGroup({
  value,
  onChange,
  groups,
  label = "Group",
  includeNoChange = false,
}: {
  value: string;
  onChange: (v: string) => void;
  groups: ReadonlyArray<{ id: string; code: string; name: string }>;
  label?: string;
  /** When true, empty value means "(no change)" and `__ungrouped__` clears. */
  includeNoChange?: boolean;
}) {
  return (
    <label className="block text-xs text-gray-600 dark:text-zinc-300">
      {label}
      <select
        className={`${inputClass} mt-1`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {includeNoChange ? (
          <option value="">(no change)</option>
        ) : null}
        <option value={includeNoChange ? "__ungrouped__" : ""}>Ungrouped</option>
        {groups.map((g) => (
          <option key={g.id} value={g.id}>
            {g.name}
          </option>
        ))}
      </select>
    </label>
  );
}
